import React, { useState, useEffect, useCallback } from 'react';
import { Card, Typography, Chip, Alert, Progress } from "@material-tailwind/react";
import {
    FaExclamationTriangle,
    FaInfoCircle,
    FaBug,
    FaFileAlt,
    FaServer,
    FaClock,
    FaShieldAlt,
    FaTachometerAlt
} from 'react-icons/fa';

const LogDashboard = ({ rootUrl, token }) => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchStats = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await fetch(`${rootUrl}/logs/stats`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                // Try to parse error message from response
                try {
                    const errorData = await response.json();
                    throw new Error(errorData.error || errorData.message || `HTTP ${response.status}: ${response.statusText}`);
                } catch (parseError) {
                    // If JSON parsing fails, use status text
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                }
            }

            const data = await response.json();
            if (data.success) {
                setStats(data.stats);
            } else {
                throw new Error(data.error || data.message || 'Failed to fetch stats');
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [rootUrl, token]);

    const getLevelIcon = (level) => {
        switch (level.toLowerCase()) {
            case 'error': return <FaExclamationTriangle className="text-red-500" />;
            case 'warn': case 'warning': return <FaExclamationTriangle className="text-amber-500" />;
            case 'info': return <FaInfoCircle className="text-blue-500" />;
            case 'debug': return <FaBug className="text-green-500" />;
            default: return <FaFileAlt className="text-gray-500" />;
        }
    };

    const getLevelColor = (level) => {
        switch (level.toLowerCase()) {
            case 'error': return 'red';
            case 'warn': case 'warning': return 'amber';
            case 'info': return 'blue';
            case 'debug': return 'green';
            default: return 'gray';
        }
    };

    const getIconBackground = (level) => {
        switch (level.toLowerCase()) {
            case 'error': return 'bg-red-100 text-red-600';
            case 'warn': case 'warning': return 'bg-amber-100 text-amber-600';
            case 'info': return 'bg-blue-100 text-blue-600';
            case 'debug': return 'bg-green-100 text-green-600';
            default: return 'bg-gray-100 text-gray-600';
        }
    };

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    if (loading) {
        return (
            <div className="mb-8">
                {/* Header Section */}
                <div className="mb-6">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div>
                                                    {/* Header */}
                        <div className="text-center mb-8">
                            <h1 className="text-4xl font-bold text-[#2a2e30] mb-2">System Logs Dashboard</h1>
                            <div className="w-24 h-1 bg-[#FF5733] mx-auto rounded-full"></div>
                        </div>
                            <Typography variant="small" color="gray" className="font-medium">
                                Loading system analytics...
                            </Typography>
                        </div>
                        <div className="flex items-center gap-2 text-blue-600">
                            <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                            <Typography variant="small" className="font-medium">Loading</Typography>
                        </div>
                    </div>
                </div>

                {/* Loading Animation */}
                <div className="flex flex-col items-center justify-center py-16">
                    <div className="relative">
                        <div className="animate-spin rounded-full h-20 w-20 border-4 border-orange-200"></div>
                        <div className="animate-spin rounded-full h-20 w-20 border-4 border-orange-500 border-t-transparent absolute top-0 left-0"></div>
                    </div>
                    <Typography variant="small" color="gray" className="mt-4 font-medium">
                        Fetching dashboard data...
                    </Typography>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="mb-8">
                {/* Header Section */}
                <div className="mb-6">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div>
                            {/* Header */}
                            <div className="text-center mb-8">
                                <h1 className="text-4xl font-bold text-[#2a2e30] mb-2">System Logs Dashboard</h1>
                                <div className="w-24 h-1 bg-[#FF5733] mx-auto rounded-full"></div>
                            </div>
                            <Typography variant="small" color="gray" className="font-medium">
                                Unable to load dashboard data
                            </Typography>
                        </div>
                        <div className="flex items-center gap-2 text-red-600">
                            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                            <Typography variant="small" className="font-medium">Error</Typography>
                        </div>
                    </div>
                </div>

                {/* Error Animation */}
                <div className="flex justify-center items-center mb-6">
                    <div className="animate-bounce p-4 bg-red-100 rounded-full">
                        <FaExclamationTriangle className="text-red-500 text-5xl" />
                    </div>
                </div>

                {/* Error Alert */}
                <Alert color="red" className="mb-8 shadow-lg">
                    <div className="flex items-start gap-3">
                        <FaExclamationTriangle className="shrink-0 mt-1" />
                        <div>
                            <Typography variant="h6" className="mb-2 font-bold">
                                Dashboard Connection Error
                            </Typography>
                            <Typography variant="small" className="break-words">
                                {error}
                            </Typography>
                        </div>
                    </div>
                </Alert>

                {/* Placeholder Cards Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 opacity-40">
                    <Card className="p-6 shadow-lg border-0 bg-gradient-to-br from-blue-50 to-indigo-50">
                        <div className="flex items-center justify-between">
                            <div>
                                <Typography variant="small" color="blue-gray" className="font-semibold mb-2 uppercase tracking-wide">
                                    Total Logs
                                </Typography>
                                <Typography variant="h3" className="text-gray-400 font-bold">
                                    --
                                </Typography>
                            </div>
                            <div className="p-3 bg-gray-300 rounded-xl text-gray-500">
                                <FaFileAlt size={24} />
                            </div>
                        </div>
                    </Card>

                    <Card className="p-6 shadow-lg border-0 bg-gradient-to-br from-green-50 to-emerald-50">
                        <div className="flex items-center justify-between">
                            <div>
                                <Typography variant="small" color="blue-gray" className="font-semibold mb-2 uppercase tracking-wide">
                                    Log Files
                                </Typography>
                                <Typography variant="h3" className="text-gray-400 font-bold">
                                    --
                                </Typography>
                            </div>
                            <div className="p-3 bg-gray-300 rounded-xl text-gray-500">
                                <FaServer size={24} />
                            </div>
                        </div>
                    </Card>

                    <Card className="p-6 shadow-lg border-0 bg-gradient-to-br from-red-50 to-rose-50">
                        <div className="flex items-center justify-between">
                            <div>
                                <Typography variant="small" color="blue-gray" className="font-semibold mb-2 uppercase tracking-wide">
                                    Errors (24h)
                                </Typography>
                                <Typography variant="h3" className="text-gray-400 font-bold">
                                    --
                                </Typography>
                            </div>
                            <div className="p-3 bg-gray-300 rounded-xl text-gray-500">
                                <FaExclamationTriangle size={24} />
                            </div>
                        </div>
                    </Card>

                    <Card className="p-6 shadow-lg border-0 bg-gradient-to-br from-amber-50 to-orange-50">
                        <div className="flex items-center justify-between">
                            <div>
                                <Typography variant="small" color="blue-gray" className="font-semibold mb-2 uppercase tracking-wide">
                                    Error Rate
                                </Typography>
                                <Typography variant="h3" className="text-gray-400 font-bold">
                                    --%
                                </Typography>
                            </div>
                            <div className="p-3 bg-gray-300 rounded-xl text-gray-500">
                                <FaTachometerAlt size={24} />
                            </div>
                        </div>
                    </Card>
                </div>
            </div>
        );
    }

    return (
        <div className="mb-8">
            {/* Header Section */}
            <div className="mb-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div>
                        {/* Header */}
                        <div className="text-center mb-8">
                            <h1 className="text-4xl font-bold text-[#2a2e30] mb-2">System Logs Dashboard</h1>
                            <div className="w-24 h-1 bg-[#FF5733] mx-auto rounded-full"></div>
                        </div>
                        <Typography variant="small" color="gray" className="font-medium">
                            Real-time monitoring and analytics for your application logs
                        </Typography>
                    </div>
                    <div className="flex items-center gap-2 text-green-600">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                        <Typography variant="small" className="font-medium">Live</Typography>
                    </div>
                </div>
            </div>

            {/* Stats Cards Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8">
                {/* Total Logs Card */}
                <Card className="p-6 shadow-lg hover:shadow-xl transition-shadow duration-300 border-0 bg-gradient-to-br from-blue-50 to-indigo-50">
                    <div className="flex items-center justify-between">
                        <div>
                            <Typography variant="small" color="blue-gray" className="font-semibold mb-2 uppercase tracking-wide">
                                Total Logs
                            </Typography>
                            <Typography variant="h3" className="text-blue-600 font-bold">
                                {stats?.totalLogs?.toLocaleString() || 0}
                            </Typography>
                        </div>
                        <div className="p-3 bg-blue-500 rounded-xl text-white">
                            <FaFileAlt size={24} />
                        </div>
                    </div>
                </Card>

                {/* Log Files Card */}
                <Card className="p-6 shadow-lg hover:shadow-xl transition-shadow duration-300 border-0 bg-gradient-to-br from-green-50 to-emerald-50">
                    <div className="flex items-center justify-between">
                        <div>
                            <Typography variant="small" color="blue-gray" className="font-semibold mb-2 uppercase tracking-wide">
                                Log Files
                            </Typography>
                            <Typography variant="h3" className="text-green-600 font-bold">
                                {stats?.logFiles || 0}
                            </Typography>
                        </div>
                        <div className="p-3 bg-green-500 rounded-xl text-white">
                            <FaServer size={24} />
                        </div>
                    </div>
                </Card>

                {/* Recent Errors Card */}
                <Card className="p-6 shadow-lg hover:shadow-xl transition-shadow duration-300 border-0 bg-gradient-to-br from-red-50 to-rose-50">
                    <div className="flex items-center justify-between">
                        <div>
                            <Typography variant="small" color="blue-gray" className="font-semibold mb-2 uppercase tracking-wide">
                                Errors (24h)
                            </Typography>
                            <Typography variant="h3" className="text-red-600 font-bold">
                                {stats?.recentErrors?.length || 0}
                            </Typography>
                        </div>
                        <div className="p-3 bg-red-500 rounded-xl text-white">
                            <FaExclamationTriangle size={24} />
                        </div>
                    </div>
                </Card>

                {/* Error Rate Card */}
                <Card className="p-6 shadow-lg hover:shadow-xl transition-shadow duration-300 border-0 bg-gradient-to-br from-amber-50 to-orange-50">
                    <div className="flex items-center justify-between">
                        <div>
                            <Typography variant="small" color="blue-gray" className="font-semibold mb-2 uppercase tracking-wide">
                                Error Rate
                            </Typography>
                            <Typography variant="h3" className={`font-bold ${(stats?.levelStats?.error || 0) > 0 ? "text-red-600" : "text-green-600"}`}>
                                {stats?.totalLogs > 0
                                    ? ((stats?.levelStats?.error || 0) / stats.totalLogs * 100).toFixed(1)
                                    : 0}%
                            </Typography>
                        </div>
                        <div className={`p-3 rounded-xl text-white ${(stats?.levelStats?.error || 0) > 0 ? "bg-red-500" : "bg-green-500"}`}>
                            <FaTachometerAlt size={24} />
                        </div>
                    </div>
                    {/* Progress bar for error rate */}
                    <div className="mt-4">
                        <Progress
                            value={stats?.totalLogs > 0 ? ((stats?.levelStats?.error || 0) / stats.totalLogs * 100) : 0}
                            color={(stats?.levelStats?.error || 0) > 0 ? "red" : "green"}
                            className="h-2"
                        />
                    </div>
                </Card>
            </div>

            {/* Analytics Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                {/* Log Levels Card */}
                <Card className="p-6 shadow-lg border-0">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 bg-purple-500 rounded-lg text-white">
                            <FaBug size={20} />
                        </div>
                        <Typography variant="h6" className="text-blue-gray-800 font-bold">
                            Log Level Distribution
                        </Typography>
                    </div>
                    <div className="space-y-4">
                        {stats?.levelStats && Object.entries(stats.levelStats)
                            .sort(([, a], [, b]) => b - a)
                            .map(([level, count]) => {
                                const percentage = stats.totalLogs > 0 ? (count / stats.totalLogs * 100) : 0;
                                return (
                                    <div key={level} className="bg-gray-50 rounded-lg p-4">
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="flex items-center gap-3">
                                                <div className={`p-2 rounded-lg ${getIconBackground(level)}`}>
                                                    {getLevelIcon(level)}
                                                </div>
                                                <div>
                                                    <Typography variant="small" className="font-semibold capitalize text-blue-gray-800">
                                                        {level}
                                                    </Typography>
                                                    <Typography variant="small" color="gray" className="text-xs">
                                                        {percentage.toFixed(1)}% of total
                                                    </Typography>
                                                </div>
                                            </div>
                                            <Chip
                                                variant="gradient"
                                                size="sm"
                                                value={count.toLocaleString()}
                                                color={getLevelColor(level)}
                                                className="font-bold"
                                            />
                                        </div>
                                        <Progress
                                            value={percentage}
                                            color={getLevelColor(level)}
                                            className="h-2"
                                        />
                                    </div>
                                );
                            })}
                    </div>
                </Card>

                {/* Log Sources Card */}
                <Card className="p-6 shadow-lg border-0">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 bg-indigo-500 rounded-lg text-white">
                            <FaServer size={20} />
                        </div>
                        <Typography variant="h6" className="text-blue-gray-800 font-bold">
                            Top Log Sources
                        </Typography>
                    </div>
                    <div className="space-y-3 max-h-80 overflow-y-auto">
                        {stats?.sourceStats && Object.entries(stats.sourceStats)
                            .sort(([, a], [, b]) => b - a)
                            .slice(0, 10) // Show top 10 sources
                            .map(([source, count], index) => {
                                const percentage = stats.totalLogs > 0 ? (count / stats.totalLogs * 100) : 0;
                                return (
                                    <div key={source} className="bg-gray-50 rounded-lg p-4">
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                                <div className="w-8 h-8 bg-gradient-to-r from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                                                    {index + 1}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <Typography variant="small" className="font-semibold text-blue-gray-800 truncate">
                                                        {source}
                                                    </Typography>
                                                    <Typography variant="small" color="gray" className="text-xs">
                                                        {percentage.toFixed(1)}% of logs
                                                    </Typography>
                                                </div>
                                            </div>
                                            <Chip
                                                variant="ghost"
                                                size="sm"
                                                value={count.toLocaleString()}
                                                color="blue"
                                                className="font-bold"
                                            />
                                        </div>
                                        <Progress
                                            value={percentage}
                                            color="blue"
                                            className="h-1.5"
                                        />
                                    </div>
                                );
                            })}
                    </div>
                </Card>
            </div>

            {/* Recent Errors Section */}
            {stats?.recentErrors && stats.recentErrors.length > 0 && (
                <Card className="p-6 shadow-lg border-0 border-l-4 border-red-500">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 bg-red-500 rounded-lg text-white">
                            <FaShieldAlt size={20} />
                        </div>
                        <div>
                            <Typography variant="h6" className="text-red-600 font-bold">
                                Critical Alerts
                            </Typography>
                            <Typography variant="small" color="gray">
                                Recent errors from the last 24 hours
                            </Typography>
                        </div>
                    </div>
                    <div className="space-y-4 max-h-96 overflow-y-auto">
                        {stats.recentErrors.slice(0, 5).map((error, index) => (
                            <div key={index} className="bg-gradient-to-r from-red-50 to-rose-50 rounded-lg p-4 border-l-4 border-red-400 hover:shadow-md transition-shadow duration-200">
                                <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                                    <div className="p-2 bg-red-100 rounded-lg text-red-600 self-start">
                                        <FaExclamationTriangle size={16} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2">
                                            <Typography variant="small" className="font-mono text-red-800 font-semibold break-words">
                                                {error.message}
                                            </Typography>
                                            <div className="flex items-center gap-2 text-gray-500 shrink-0">
                                                <FaClock size={12} />
                                                <Typography variant="small" className="text-xs font-medium">
                                                    {new Date(error.timestamp).toLocaleString()}
                                                </Typography>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Chip
                                                variant="ghost"
                                                size="sm"
                                                value={error.source}
                                                color="red"
                                                className="text-xs"
                                            />
                                            <Typography variant="small" color="gray" className="text-xs">
                                                Error #{index + 1}
                                            </Typography>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                        {stats.recentErrors.length > 5 && (
                            <div className="text-center pt-4">
                                <Typography variant="small" color="gray" className="italic">
                                    + {stats.recentErrors.length - 5} more errors in the last 24 hours
                                </Typography>
                            </div>
                        )}
                    </div>
                </Card>
            )}

            {/* Empty State for No Errors */}
            {stats?.recentErrors && stats.recentErrors.length === 0 && (
                <Card className="p-8 shadow-lg border-0 border-l-4 border-green-500 text-center">
                    <div className="flex flex-col items-center gap-4">
                        <div className="p-4 bg-green-100 rounded-full text-green-600">
                            <FaShieldAlt size={32} />
                        </div>
                        <div>
                            <Typography variant="h6" className="text-green-600 font-bold mb-2">
                                All Systems Operational
                            </Typography>
                            <Typography variant="small" color="gray">
                                No errors detected in the last 24 hours. Your system is running smoothly!
                            </Typography>
                        </div>
                    </div>
                </Card>
            )}
        </div>
    );
};

export default LogDashboard;
